// src/components/dashboard/MesDemandesCard.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabaseClient";
import { useAuth } from "@/context/AuthContext";
import { useTranslation } from "react-i18next";

function StatusBadge({ status }) {
  const { t } = useTranslation();

  const mapColors = {
    paid: "bg-green-100 text-green-800",
    pending: "bg-amber-100 text-amber-800",
    awaiting_payment: "bg-amber-100 text-amber-800",
    cancelled: "bg-rose-100 text-rose-800",
    canceled: "bg-rose-100 text-rose-800",
    rejected: "bg-rose-100 text-rose-800",
    sent: "bg-blue-100 text-blue-800",
    in_progress: "bg-blue-100 text-blue-800",
    completed: "bg-green-100 text-green-800",
    draft: "bg-gray-100 text-gray-700",
  };

  const cls = mapColors[status] || "bg-gray-100 text-gray-700";

  const statusKey = String(status || "unknown");
  const label = t(`mesDemandes.status.${statusKey}`, {
    defaultValue: t("mesDemandes.status.unknown", {
      status: status || "—",
    }),
  });

  return (
    <span
      className={`inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium ${cls}`}
    >
      {label}
    </span>
  );
}

export default function MesDemandesCard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);

  const fdate = (iso) => {
    if (!iso) return "";
    const date = new Date(iso);
    if (Number.isNaN(date.getTime())) return "";

    return new Intl.DateTimeFormat(
      i18n.resolvedLanguage || i18n.language || undefined,
      { day: "2-digit", month: "short", year: "numeric" }
    ).format(date);
  };

  // charge les 5 dernières demandes de l'utilisateur
  useEffect(() => {
    let ignore = false;
    const run = async () => {
      if (!user?.id) return;
      setLoading(true);
      setLoadError(false);

      const { data, error } = await supabase
        .from("proposals")
        .select(
          "id, artwork_id, institution, proposal_type, send_method, status, created_at"
        )
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(5);

      if (!ignore) {
        if (error) {
          console.error("MesDemandesCard fetch error:", error);
          setRows([]);
          setLoadError(true);
        } else {
          setRows(data || []);
          setLoadError(false);
        }
        setLoading(false);
      }
    };
    run();
    return () => {
      ignore = true;
    };
  }, [user?.id]);

  const hasRows = rows && rows.length > 0;

  const counters = useMemo(() => {
    const c = { paid: 0, pending: 0, sent: 0, draft: 0 };
    rows.forEach((r) => {
      if (r.status === "awaiting_payment") {
        c.pending += 1;
      } else {
        c[r.status] = (c[r.status] || 0) + 1;
      }
    });
    return c;
  }, [rows]);

  const gotoAll = () => navigate("/proposals");
  const gotoItem = (row) =>
    navigate(`/propose/${row.artwork_id}`, {
      state: {
        proposalId: row.id,
        artworkId: row.artwork_id,
        institution: row.institution,
        proposalType: row.proposal_type,
      },
    });

  const getProposalTypeLabel = (proposalType) =>
    t(`mesDemandes.type.${proposalType || "unknown"}`, {
      defaultValue: t("mesDemandes.type.unknown"),
    });

  const getSendMethodLabel = (sendMethod) =>
    t(`mesDemandes.sendMethod.${sendMethod || "unknown"}`, {
      defaultValue: t("mesDemandes.sendMethod.unknown"),
    });

  return (
    <div className="p-6 bg-white rounded-2xl border border-[#e8e3d8]">
      <div className="flex items-center justify-between mb-1">
        <h2 className="text-xl md:text-2xl font-light text-gray-900" style={{ fontFamily: "Georgia, serif" }}>
          {t("mesDemandes.title")}
        </h2>
        <button
          type="button"
          onClick={gotoAll}
          aria-label={t("mesDemandes.seeAll")}
          className="text-sm text-indigo-700 hover:underline"
        >
          {t("mesDemandes.seeAll")}
        </button>
      </div>
      <p className="text-xs text-gray-500 mb-3">{t("mesDemandes.subtitle")}</p>

      {/* mini stats */}
      <div className="flex gap-3 text-xs mb-4">
        <span className="px-2 py-1 bg-amber-50 text-amber-800 rounded">
          {t("mesDemandes.stats.pending", {
            count: counters.pending || 0,
          })}
        </span>
        <span className="px-2 py-1 bg-blue-50 text-blue-800 rounded">
          {t("mesDemandes.stats.sent", {
            count: counters.sent || 0,
          })}
        </span>
        <span className="px-2 py-1 bg-green-50 text-green-800 rounded">
          {t("mesDemandes.stats.paid", {
            count: counters.paid || 0,
          })}
        </span>
        <span className="px-2 py-1 bg-gray-50 text-gray-700 rounded">
          {t("mesDemandes.stats.draft", {
            count: counters.draft || 0,
          })}
        </span>
      </div>

      {loading ? (
        <div className="text-sm text-gray-500">
          {t("mesDemandes.loading")}
        </div>
      ) : loadError ? (
        <div className="text-sm text-red-600" role="alert">
          {t("mesDemandes.error")}
        </div>
      ) : !hasRows ? (
        <div className="text-sm text-gray-500">
          {t("mesDemandes.empty")}
        </div>
      ) : (
        <div className="divide-y">
          {rows.map((r) => (
            <div
              key={r.id}
              className="py-3 flex items-center justify-between"
            >
              <div className="min-w-0">
                <div className="text-sm font-medium truncate">
                  {r.institution ||
                    t("mesDemandes.institutionFallback")}
                </div>
                <div className="text-xs text-gray-500">
                  {fdate(r.created_at)} •{" "}
                  {getProposalTypeLabel(r.proposal_type)}
                  {" • "}
                  {getSendMethodLabel(r.send_method)}
                </div>
              </div>
              <div className="flex items-center gap-3">
                <StatusBadge status={r.status} />
                <button
                  type="button"
                  onClick={() => gotoItem(r)}
                  aria-label={t("mesDemandes.view.title")}
                  className="text-sm px-2.5 py-1 border rounded hover:bg-gray-50"
                  title={t("mesDemandes.view.title")}
                >
                  {t("mesDemandes.view.label")}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
